/**
 * Memento-urile unei întâlniri: cui, cât înainte, pe ce canal.
 *
 * Alexandru, 01.10.2026: „posibilitate de adăugare notificări (notificare în aplicație, mail),
 * frecvență, numărul notificărilor, ceva similar cu Google Calendar".
 *
 * „Frecvența și numărul" sunt un AJUTOR DE COMPLETARE, nu o regulă stocată: alegi „la fiecare zi,
 * de 3 ori" și îți scrie cele trei memento-uri, pe care apoi le poți șterge una câte una. Dacă
 * s-ar fi stocat regula, un singur memento n-ar fi putut fi șters fără să le strice pe celelalte,
 * iar la întrebarea „ce s-a trimis și când" ar fi răspuns o formulă, nu un tabel.
 *
 * Destinatarii sunt utilizatori din CRM, separat de câmpul „Participanți", care rămâne text
 * liber: acela spune CINE VINE, ăsta spune CINE E ANUNȚAT.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

export interface Memento {
  id?: number
  minuteInainte: number
  canal: 'app' | 'email'
  trimisLa?: string | null
  eroare?: string | null
}

/** Cât înainte, scris pe românește. Aceeași formulare ca în mementoul care chiar pleacă. */
export function cuCatInainte(minute: number): string {
  if (minute <= 0) return 'la începere'
  if (minute < 60) return `${minute} min înainte`
  if (minute < 24 * 60) {
    const ore = Math.round(minute / 60)
    return ore === 1 ? 'cu o oră înainte' : `cu ${ore} ore înainte`
  }
  const zile = Math.round(minute / (24 * 60))
  return zile === 1 ? 'cu o zi înainte' : `cu ${zile} zile înainte`
}

/** Pașii din care se alege repede, ca în Google Calendar. */
const PRESETARI = [
  { minute: 15, eticheta: '15 min' },
  { minute: 30, eticheta: '30 min' },
  { minute: 60, eticheta: '1 oră' },
  { minute: 24 * 60, eticheta: '1 zi' },
  { minute: 2 * 24 * 60, eticheta: '2 zile' },
  { minute: 7 * 24 * 60, eticheta: '1 săptămână' },
]

const CANALE: { cheie: 'app' | 'email'; eticheta: string }[] = [
  { cheie: 'app', eticheta: 'în aplicație' },
  { cheie: 'email', eticheta: 'email' },
]

const MAX_MINUTE = 30 * 24 * 60

export function BlocMementouri({
  areOra,
  destinatari,
  setDestinatari,
  mementouri,
  setMementouri,
}: {
  areOra: boolean
  destinatari: number[]
  setDestinatari: (v: number[]) => void
  mementouri: Memento[]
  setMementouri: (v: Memento[]) => void
}) {
  /*
    DOUA BIFE, nu un comutator.

    Prima varianta avea un comutator intre „in aplicatie" si „email", iar ca sa ai amandoua
    trebuia sa apesi presetarea de doua ori, mutand comutatorul intre ele. Functiona, dar arata a
    MOD, nu a bifa — Alexandru a intrebat direct „cum fac daca vreau memento si in aplicatie si
    in mail", ceea ce e raspunsul: nimeni n-ar fi ghicit.

    Acum o presetare apasata scrie cate un memento pentru fiecare canal bifat.
  */
  const [canale, setCanale] = useState<Record<'app' | 'email', boolean>>({
    app: true,
    email: false,
  })
  const alese = (Object.keys(canale) as ('app' | 'email')[]).filter((c) => canale[c])
  const [frecventa, setFrecventa] = useState('1440')
  const [cateOri, setCateOri] = useState('3')

  const utilizatori = useQuery<{ users: { id: number; name: string | null; email: string }[] }>({
    queryKey: ['mentionable-users'],
    queryFn: async () => (await api.get('/tasks/mentionable-users')).data,
  })

  /** O presetare apasata scrie cate un memento pe fiecare canal bifat. */
  const adauga = (minuteInainte: number) => {
    const noi = [...mementouri]
    for (const c of alese) {
      if (noi.some((m) => m.minuteInainte === minuteInainte && m.canal === c)) continue
      noi.push({ minuteInainte, canal: c })
    }
    setMementouri(noi)
  }

  /* „La fiecare X, de N ori" → N memento-uri la X, 2X, 3X… Scrie rânduri, nu o regulă. */
  const adaugaSerie = () => {
    const pas = Number(frecventa)
    const ori = Number(cateOri)
    if (!Number.isFinite(pas) || pas <= 0 || !Number.isFinite(ori) || ori <= 0) return
    const noi = [...mementouri]
    for (let i = 1; i <= Math.min(ori, 10); i++) {
      const minute = pas * i
      if (minute > MAX_MINUTE) break
      for (const c of alese) {
        if (noi.some((m) => m.minuteInainte === minute && m.canal === c)) continue
        noi.push({ minuteInainte: minute, canal: c })
      }
    }
    setMementouri(noi)
  }

  const sortate = [...mementouri].sort((a, b) => a.minuteInainte - b.minuteInainte)
  const nrUtilizatori = utilizatori.data?.users.length ?? 3

  return (
    <div className="space-y-2 rounded border border-b4l-border bg-b4l-bg-soft p-3">
      <div className="text-xs font-medium text-b4l-text-muted">Memento-uri</div>

      <label className="block text-xs text-b4l-text-muted">
        Colegii care participă și primesc memento-urile
        <select
          multiple
          size={Math.min(5, Math.max(3, nrUtilizatori))}
          value={destinatari.map(String)}
          onChange={(e) =>
            setDestinatari([...e.target.selectedOptions].map((o) => Number(o.value)))
          }
          className="mt-1 w-full rounded border border-b4l-border px-2 py-1 text-sm"
        >
          {(utilizatori.data?.users ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name ?? u.email}
            </option>
          ))}
        </select>
        <span className="mt-0.5 block text-[11px] text-b4l-text-dim">
          Ține apăsat Ctrl ca să alegi mai mulți. Cine nu are cont în CRM — un partener, o
          farmacistă — se scrie mai sus, la „Alți participanți&rdquo;, și nu primește memento.
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {CANALE.map((c) => (
          <label key={c.cheie} className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={canale[c.cheie]}
              onChange={(e) => setCanale((x) => ({ ...x, [c.cheie]: e.target.checked }))}
            />
            {c.eticheta}
          </label>
        ))}
        <span className="text-b4l-text-dim">
          {alese.length === 2
            ? '— se adaugă pe amândouă'
            : alese.length === 0
              ? '— bifează măcar un canal'
              : ''}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1 text-xs">
        {PRESETARI.map((p) => (
          <button
            key={p.minute}
            type="button"
            disabled={alese.length === 0}
            onClick={() => adauga(p.minute)}
            className="rounded border border-b4l-border bg-white px-2 py-1 hover:bg-b4l-bg disabled:opacity-40"
          >
            + {p.eticheta}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1 text-xs text-b4l-text-muted">
        <span>sau: la fiecare</span>
        <select
          value={frecventa}
          onChange={(e) => setFrecventa(e.target.value)}
          className="rounded border border-b4l-border px-1 py-0.5"
        >
          <option value="60">oră</option>
          <option value="1440">zi</option>
          <option value="10080">săptămână</option>
        </select>
        <span>, de</span>
        <input
          type="number"
          min={1}
          max={10}
          value={cateOri}
          onChange={(e) => setCateOri(e.target.value)}
          className="w-12 rounded border border-b4l-border px-1 py-0.5 text-right"
        />
        <span>ori</span>
        <button
          type="button"
          disabled={alese.length === 0}
          onClick={adaugaSerie}
          className="rounded border border-b4l-border bg-white px-2 py-0.5 hover:bg-b4l-bg disabled:opacity-40"
        >
          Adaugă
        </button>
      </div>

      {sortate.length === 0 ? (
        <p className="text-[11px] text-b4l-text-dim">
          Niciun memento. Fără el, întâlnirea stă doar în listă.
        </p>
      ) : (
        <ul className="space-y-0.5">
          {sortate.map((m) => (
            <li
              key={`${m.minuteInainte}-${m.canal}`}
              className="flex items-center gap-2 rounded bg-white px-2 py-1 text-xs"
            >
              <span className="flex-1">
                {cuCatInainte(m.minuteInainte)} · {m.canal === 'app' ? 'în aplicație' : 'pe email'}
              </span>
              {/*
                Un memento trimis nu se mai poate șterge: e o urmă, nu o intenție. Dacă ar
                dispărea, același memento ar putea pleca a doua oară după o editare.
              */}
              {m.trimisLa ? (
                <span className="text-b4l-green-dark" title="Deja trimis.">
                  trimis
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setMementouri(
                      mementouri.filter(
                        (x) => !(x.minuteInainte === m.minuteInainte && x.canal === m.canal),
                      ),
                    )
                  }
                  className="text-b4l-text-muted hover:text-b4l-red"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!areOra && sortate.length > 0 && (
        <p className="text-[11px] text-b4l-amber-dark">
          Întâlnirea n-are oră, deci memento-urile se socotesc de la <strong>ora 9:00</strong> în
          ziua ei. Scrie o oră dacă vrei altfel.
        </p>
      )}
    </div>
  )
}
